import type { LabTestStatus } from "@/models/LabTestStatus";
import type { ClassifiedSoil, SoilFractionsContract, SoilStandard } from "./common/soilFractions";
import type { EquipmentUsedEntry } from "./equipmentUsed";
import type { StateTransitionContract } from "./stateTransitions";

export interface LaboratoryInfo {
  id: string;
  name: string | null;
}

/**
 * Physical container the sample arrived in.
 * Canonical values match the backend `SampleType` enum; the generated import template
 * writes them capitalised and `mapSampleType` parses them back, so they are not localised.
 */
export const SAMPLE_TYPES = [
  "bag",
  "wax",
  "liner",
  "tube",
  "jar",
  "quart",
  "bucket",
  "batch",
  "block",
  "core",
] as const;

export type SampleType = (typeof SAMPLE_TYPES)[number];

export interface SampleInfo {
  id: string;
  fugroSampleId: string | null;
  name: string;
  topDepthMeters?: number;
  location?: string;
  condition?: "disturbed" | "undisturbed" | null;
  type?: SampleType | null;
  soilFractions?: SoilFractionsContract | null;
  classifiedSoil?: ClassifiedSoil | null;
  classifiedSoilStandard?: SoilStandard | null;
}

export interface ProjectInfo {
  id: string;
  goldenRecordId: string;
  number?: string | null;
  name: string;
  contract?: string;
  client?: string | null;
}

export interface LabTestMetadata {
  id: string;
  type: string;
  method: string;
  status: LabTestStatus;
  createdAt: string;
  processingTestId?: string | null;
  isRetest?: boolean;
  stateTransition?: StateTransitionContract;
  laboratory: LaboratoryInfo;
  sample?: SampleInfo;
  project?: ProjectInfo;
  specimenReference?: string;
  specimenDepth?: number | null;
  equipmentUsed?: EquipmentUsedEntry[];
  displayUnits?: Record<string, string>;
  soilDescription?: string | null;
  assignmentRemarks?: string | null;
}

export interface TestSpecimen {
  specimenReference: string;
  specimenDepth: number | null;
}

export const asDisplayUnits = (chosen: unknown): Record<string, string> =>
  typeof chosen === "object" && chosen !== null
    ? Object.fromEntries(Object.entries(chosen).filter(([, unitId]) => typeof unitId === "string" && unitId !== ""))
    : {};

export const readPersistedDisplayUnits = (data: Record<string, unknown> | null): Record<string, string> =>
  asDisplayUnits(data?.displayUnits);
